import React from 'react'
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    Font,
    Image,
} from '@react-pdf/renderer'
import { QuizForm } from '@interfaces/Quiz'
import LOGO from '../images/MTLLOGO.jpg'
import ARROW from '../images/ARROW.jpg'
import F_REGULAR from './font/KaLaTeXa_ Font/KaLaTeXaTEXT_Regular4.2.ttf'
import F_MEDIUM from './font/KaLaTeXa_ Font/KaLaTeXaTEXT_Bold.ttf'
import F_BOLD from './font/KaLaTeXa_ Font/KaLaTeXaTEXT_ExtraBold.ttf'
import Money from './Money'

Font.register({
    family: 'KaLaTeXa, sans-serif',
    src: F_REGULAR as string, // Replace with the actual font file URL
    fontWeight: 'normal', // You can specify the font weight if necessary
})

Font.register({
    family: 'KaLaTeXa, sans-serif',
    src: F_MEDIUM as string, // Replace with the actual font file URL
    fontWeight: 'medium', // You can specify the font weight if necessary
})

Font.register({
    family: 'KaLaTeXa, sans-serif',
    src: F_BOLD as string, // Replace with the actual font file URL
    fontWeight: 'bold', // You can specify the font weight if necessary
})

// Create styles
const styles = StyleSheet.create({
    body: {
        fontFamily: 'KaLaTeXa, sans-serif',
    },
    page: {
        padding: 40,
        height: 500,
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
    },
    section: {
        marginBottom: 20,
    },
    text: {
        fontFamily: 'KaLaTeXa, sans-serif',
    },
    header1: {
        fontFamily: 'KaLaTeXa, sans-serif',
        fontSize: 14,
        color: '#e6007e',
        fontWeight: 'bold',
    },
    concludeHeader: {
        fontFamily: 'KaLaTeXa, sans-serif',
        fontSize: 14,
        marginBottom: 6,
        color: '#4C4C4C',
        fontWeight: 'bold',
    },
    concludeGreen: {
        fontFamily: 'KaLaTeXa, sans-serif',
        fontSize: 14,
        color: '#71BB70',
    },
    concludeRed: {
        fontFamily: 'KaLaTeXa, sans-serif',
        fontSize: 14,
        color: '#C10D0D',
    },
    subheader: {
        fontFamily: 'KaLaTeXa, sans-serif',
        color: '#B0B0B0',
        fontWeight: 'normal',
        fontSize: 12,
    },
    flexConcludeHeader: {
        flexDirection: 'row',
        alignContent: 'center',
        gap: 10,
    },
    flexSpaceBetween: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    logo: {
        width: 65,
    },

    dottedLine: {
        marginVertical: 16,
        color: '#B1B0B0',
        letterSpacing: 3,
        fontSize: 10,
    },
    arrow: {
        height: 10,
        marginTop: 5,
    },
    contentConcludeContainer: {
        paddingLeft: 19,
    },
    concludeContent: {
        fontFamily: 'KaLaTeXa, sans-serif',
        fontSize: 14,
        color: '#4C4C4C',
    },
    flexColumn1: {
        marginTop: 4,
        flexDirection: 'column',
        width: 140,
        gap: 14,
    },
    flexColumn2: {
        marginTop: 4,
        flexDirection: 'column',
        width: 125,
        gap: 14,
    },
    flexRow: {
        flexDirection: 'row',
        gap: 60,
    },
    textCenter: {
        color: '#4C4C4C',
        fontFamily: 'KaLaTeXa, sans-serif',
        fontSize: 14,
        textAlign: 'center',
    },
    textBottom: {
        fontFamily: 'KaLaTeXa, sans-serif',
        color: '#B0B0B0',
        fontWeight: 'normal',
        fontSize: 10,
    },
    flexBottom: {
        marginTop: 'auto',
    },
})

// Create Document Component
const PDFGenerator = ({ data }: { data: QuizForm }) => {
    const {
        retirement_age,
        payment_after_retirement_each_month,
        payment_after_retirement_each_year,
        long_term_inflation_assumption,
        payment_at_retirement_first_year,
        expected_rate_of_return_before_retirement,
        required_funds_for_retirement,
        estimated_rate_return_after_retirement,
        estimated_saving_source_for_retirement,
        dificit_funds_retirement,
        investment_rate_for_expect_return_two_percent,
        investment_rate_for_expect_return_four_percent,
        investment_rate_for_expect_return_six_percent,
        investment_rate_for_expect_return_eight_percent,
        person_type,
        person_id,
    } = data

    const suggests = {
        column1: ['2.00%', '4.00%', '6.00%', '8.00%'],
        column2: [
            investment_rate_for_expect_return_two_percent,
            investment_rate_for_expect_return_four_percent,
            investment_rate_for_expect_return_six_percent,
            investment_rate_for_expect_return_eight_percent,
        ],
    }

    return (
        <Document>
            <Page size='A4' style={styles.page}>
                <View style={styles.flexSpaceBetween}>
                    <View>
                        <Text style={styles.header1}>
                            สรุปผลการวิเคราะห์การคำนวณเงินทุนเพื่อการเกษียณอายุ
                        </Text>
                        <Text style={styles.subheader}>
                            แบบประเมินเงินออมเพื่อรองรับการเกษียณอายุ
                        </Text>
                    </View>
                    <View>
                        <Image src={LOGO as any} style={styles.logo} />
                    </View>
                </View>
                <View>
                    <Text style={styles.dottedLine}>
                        ----------------------------------------------------------------------------------
                    </Text>
                </View>
                <View style={styles.section}>
                    <View style={styles.flexConcludeHeader}>
                        <Text style={styles.concludeHeader}>
                            {person_type! === 1
                                ? 'รหัสพนักงาน : '
                                : 'เลขประจำตัวประชาชน 5 ตัวท้าย : '}
                            {person_id}
                        </Text>
                    </View>
                    <View style={styles.flexConcludeHeader}>
                        <Image src={ARROW} style={styles.arrow} />
                        <Text style={styles.concludeHeader}>
                            การวิเคราะห์เป้าหมายทางการเงินที่ต้องการเพื่อการเกษียณ
                        </Text>
                    </View>
                    <View style={styles.contentConcludeContainer}>
                        <Text style={styles.concludeContent}>
                            คุณต้องการเกษียณเมื่ออายุ {retirement_age} ปี
                            และมีเงินใช้จ่ายหลังเกษียณ{' '}
                            {Money(payment_after_retirement_each_month!)}{' '}
                            บาท/เดือน หรือ{' '}
                            {Money(payment_after_retirement_each_year!)} บาท/ปี
                            (มูลค่าในปัจจุบัน) จากการวิเคราะห์พบว่า
                            ที่อัตราเงินเฟ้อ {long_term_inflation_assumption}%
                            ต่อปี
                            จำนวนเงินที่ต้องการมีไว้ใช้จ่ายเมื่อเกษียณอายุในปีแรกที่เกษียณอายุ
                            เท่ากับ {Money(payment_at_retirement_first_year!)}{' '}
                            บาท/ปี ที่อัตราผลตอบแทนคาดหวังหลังเกษียณ{' '}
                            {estimated_rate_return_after_retirement}% ต่อปี
                            เงินทุนเพื่อการเกษียณทั้งหมดที่ต้องมี ณ
                            ปีแรกที่เกษียณอายุ เท่ากับ{' '}
                            {Money(required_funds_for_retirement!)} บาท
                        </Text>
                    </View>
                </View>
                <View style={styles.section}>
                    <View style={styles.flexConcludeHeader}>
                        <Image src={ARROW} style={styles.arrow} />
                        <Text style={styles.concludeHeader}>
                            การวิเคราะห์แหล่งเงินออมเพื่อการเกษียณ
                        </Text>
                    </View>
                    <View style={styles.contentConcludeContainer}>
                        <Text style={styles.concludeContent}>
                            จากการประมาณการที่อัตราผลตอบแทนคาดหวังก่อนเกษียณ{' '}
                            {expected_rate_of_return_before_retirement}% ต่อปี
                            พบว่า แหล่งเงินออมเพิ่อการเกษียณที่เตรียมไว้แล้ว ณ
                            ปีที่เกษียณอายุ จะมีมูลค่าเท่ากับ
                        </Text>
                        <Text style={styles.concludeContent}>
                            {Money(estimated_saving_source_for_retirement!)} บาท
                        </Text>
                    </View>
                </View>
                <View style={styles.section}>
                    <View style={styles.flexConcludeHeader}>
                        <Image src={ARROW} style={styles.arrow} />
                        <Text style={styles.concludeHeader}>
                            การวิเคราะห์และส่วนขาดเกินของแหล่งเงินออม
                        </Text>
                    </View>
                    <View style={styles.contentConcludeContainer}>
                        {dificit_funds_retirement! > 0 ? (
                            <>
                                <Text style={styles.concludeRed}>
                                    จากการคำนวนพบว่า
                                    คุณมีส่วนขาดของเงินทุนเพื่อการเกษียณที่ต้องการ{' '}
                                </Text>
                                <Text style={styles.concludeRed}>
                                    {Money(dificit_funds_retirement!)} บาท บาท
                                </Text>
                            </>
                        ) : (
                            <Text style={styles.concludeGreen}>
                                จากการคำนวนพบว่า
                                คุณไม่มีส่วนขาดของเงินทุนเพื่อการเกษียณณ
                            </Text>
                        )}
                    </View>
                </View>
                {dificit_funds_retirement! > 0 && (
                    <View style={styles.section}>
                        <View style={styles.flexConcludeHeader}>
                            <Image src={ARROW} style={styles.arrow} />
                            <Text style={styles.concludeHeader}>
                                คำแนะนำเพิ่มเติมมม
                            </Text>
                        </View>
                        <View style={styles.contentConcludeContainer}>
                            <Text style={styles.concludeContent}>
                                ท่านควรนำเงินมาออมและลงทุนเพิ่มเติมเพื่อบรรลุเป้าหมายการเกษียณอายุ
                            </Text>
                            <View style={styles.flexRow}>
                                <View style={styles.flexColumn1}>
                                    <Text style={styles.concludeContent}>
                                        อัตราผลตอบแทนคาดหวัง
                                    </Text>
                                    {suggests.column1.map((value, index) => (
                                        <Text
                                            key={index}
                                            style={styles.textCenter}
                                        >
                                            {value}
                                        </Text>
                                    ))}
                                </View>
                                <View style={styles.flexColumn2}>
                                    <Text style={styles.concludeContent}>
                                        เงินลงทุน (บาท/เดือน)
                                    </Text>
                                    {suggests.column2.map((value, index) => (
                                        <Text
                                            key={index}
                                            style={styles.textCenter}
                                        >
                                            {Money(value!)}
                                        </Text>
                                    ))}
                                </View>
                            </View>
                        </View>
                    </View>
                )}
                <View style={styles.flexBottom}>
                    <Text style={styles.textBottom}>
                        หมายเหตุ:
                        ผลลัพธ์ที่ได้จากการคำนวณเป็นเพียงตัวเลขประมาณการเท่านั้น{' '}
                        โดยใช้สูตรคำนวณตามหลักการวางแผนการเงินส่วนบุคคลล
                    </Text>
                </View>
            </Page>
        </Document>
    )
}

export default PDFGenerator
